import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';

import { cancelMemberPointGrant, getBranchPointHistories, getBranchPointSetting } from '../../../api/member-point';
import PointGrantModal from './PointGrantModal';
import BulkBatchListModal from './BulkBatchListModal';
import PointHistoryEditModal from '../../userDetails/UserPointHistory/PointHistoryEditModal';
import PointSummaryCards from '../../point/PointSummaryCards';
import PointHistorySection from '../../point/PointHistorySection';
import PointSettingModal from './PointSettingModal';
import {
  PageContainer,
  SettingTitleButton,
  SectionTitleRow,
  SelectBranchNotice,
  SettingStatusText,
  SettingsRow,
  SummaryCardsStretch,
  GroupGrantRow,
  GroupGrantButton,
} from './PointManagementStyled';

const ENABLED = 'Y';

// 요약 카드(총 적립/차감)만 필요하므로 최소 사이즈로 조회
const SUMMARY_QUERY_PARAMS = { type: 'ALL', page: 1, limit: 1 };

const PointManagement = ({ branchId: propBranchId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);

  const branchId = propBranchId || user?.branch?.branchId;
  const token = user?.token;

  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const { data: pointSetting } = useQuery({
    queryKey: ['branchPointSetting', branchId],
    queryFn: () => getBranchPointSetting(branchId, token),
    enabled: !!branchId && !!token,
  });

  const { data: summary } = useQuery({
    queryKey: ['branchPointHistories', branchId, 'summary'],
    queryFn: () => getBranchPointHistories(branchId, qs.stringify(SUMMARY_QUERY_PARAMS), token),
    enabled: !!branchId && !!token,
  });


  const fetchHistories = queryString => getBranchPointHistories(branchId, queryString, token);

  const handleMemberClick = row => {
    if (!row?.memberId) return;
    navigate(`/member/${row.memberId}?tab=ten`);
  };

  if (!branchId) {
    return <SelectBranchNotice>{t('지점을 선택해주세요')}</SelectBranchNotice>;
  }

  const enabled = pointSetting?.enabledYn === ENABLED;

  return (
    <PageContainer>
      <SectionTitleRow>
        <SettingTitleButton type="button" onClick={() => setSettingModalOpen(true)}>
          {t('포인트 적립 설정')}
          <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
        </SettingTitleButton>
        <SettingStatusText $enabled={enabled}>{enabled ? t('사용 중') : t('꺼짐')}</SettingStatusText>

        {/* 적립이 꺼진 지점에서는 일괄 적립/차감도 막는다(사용자 확정). 서버도 같은 조건으로 거부한다. */}
        {enabled && (
          <GroupGrantRow>
            <GroupGrantButton type="button" onClick={() => setBatchModalOpen(true)} style={{ background: '#fff', color: '#2C3E50', border: '1px solid #C9CED6' }}>
              {t('일괄 지급/차감 내역')}
            </GroupGrantButton>
            <GroupGrantButton type="button" onClick={() => setGrantModalOpen(true)}>
              <LoyaltyOutlinedIcon sx={{ fontSize: 18 }} />
              {t('포인트 적립/차감')}
            </GroupGrantButton>
          </GroupGrantRow>
        )}
      </SectionTitleRow>

      <SettingsRow>
        <SummaryCardsStretch>
          <PointSummaryCards totalEarnPoint={summary?.totalEarnPoint} totalDeductPoint={summary?.totalDeductPoint} />
        </SummaryCardsStretch>
      </SettingsRow>

      <PointHistorySection
        fetchHistories={fetchHistories}
        queryKeyPrefix={['branchPointHistories', branchId]}
        showSearch
        showPeriodPreset
        onMemberClick={handleMemberClick}
        enabled={!!branchId && !!token}
        excelFilename="지점_포인트내역"
        onCancelGrant={row => cancelMemberPointGrant(row.memberId, row.pointHistoryId, null, token)}
        onEditHistory={row => setEditRow(row)}
      />

      <PointHistoryEditModal
        open={!!editRow}
        onClose={() => setEditRow(null)}
        row={editRow}
        memberId={editRow?.memberId}
        branchId={branchId}
        token={token}
      />

      <BulkBatchListModal
        open={batchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        branchId={branchId}
        token={token}
      />

      <PointGrantModal
        open={grantModalOpen}
        onClose={() => setGrantModalOpen(false)}
        branchId={branchId}
        token={token}
      />

      <PointSettingModal
        open={settingModalOpen}
        onClose={() => setSettingModalOpen(false)}
        branchId={branchId}
        token={token}
        pointSetting={pointSetting}
      />
    </PageContainer>
  );
};

export default PointManagement;
